import * as XLSX from 'xlsx'
import { DataTableColumn } from './DataTable.types'

export interface ExportOptions<T> {
  data: T[]
  columns: DataTableColumn<T>[]
  filename?: string
  sheetName?: string
  getExportData?: (
    items: T[],
    columns: DataTableColumn<T>[]
  ) => Record<string, any>[]
}

export const exportToExcel = <T>({
  data,
  columns,
  filename = 'export',
  sheetName = 'Data',
  getExportData,
}: ExportOptions<T>) => {
  // Build export data
  let exportData: Record<string, any>[]

  if (getExportData) {
    exportData = getExportData(data, columns)
  } else {
    // Default export - use column labels and exportFormatter if provided
    exportData = data.map((item) => {
      const rowData: Record<string, any> = {}

      columns.forEach((column) => {
        const value = column.exportFormatter
          ? column.exportFormatter(item)
          : (item as any)[column.key]

        rowData[column.label] = value ?? ''
      })

      return rowData
    })
  }

  // Create workbook and worksheet
  const wb = XLSX.utils.book_new()
  const ws = XLSX.utils.json_to_sheet(exportData)

  // Auto-size columns based on content
  const colWidths = columns.map((column) => ({
    wch:
      Math.max(
        column.label.length,
        ...data.map((item) => {
          const value = column.exportFormatter
            ? column.exportFormatter(item)
            : (item as any)[column.key]
          return String(value ?? '').length
        })
      ) + 2,
  }))

  ws['!cols'] = colWidths

  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(wb, ws, sheetName)

  // Generate Excel file
  const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
  const blob = new window.Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })

  // Download the file
  const link = document.createElement('a')
  const url = window.URL.createObjectURL(blob)
  const date = new Date().toISOString().split('T')[0]

  link.setAttribute('href', url)
  link.setAttribute('download', `${filename}_${date}.xlsx`)
  link.style.visibility = 'hidden'

  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  // Clean up
  window.URL.revokeObjectURL(url)
}
